import { useState } from "react";
import axios from "axios";
import {
  Trash2,
  TreeDeciduous,
  HeartHandshake,
  Bird,
  Flame,
  Wrench,
  CircleHelp,
  MapPin,
  Send,
  Check,
  Loader2,
  AlertCircle,
  ArrowRight,
} from "lucide-react";
import { WHATSAPP_LINK } from "../constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const REPORT_CATEGORIES = [
  {
    id: "zona_da_pulire",
    label: "Zona da pulire",
    blurb: "Aree con rifiuti, sporco o residui dopo l'incendio",
    icon: Trash2,
    accent: "#2E4F3B",
  },
  {
    id: "potature_smaltimento",
    label: "Potature da smaltire",
    blurb: "Hai sfalci o ramaglie? Le ritiriamo senza fuoco.",
    icon: TreeDeciduous,
    accent: "#2E4F3B",
  },
  {
    id: "persona_difficolta",
    label: "Persona in difficoltà",
    blurb: "Anziani soli, famiglie colpite, situazioni urgenti",
    icon: HeartHandshake,
    accent: "#C15C3D",
  },
  {
    id: "animale_difficolta",
    label: "Animale in difficoltà",
    blurb: "Fauna ferita, smarrita o senza cibo/acqua",
    icon: Bird,
    accent: "#2E4F3B",
  },
  {
    id: "pericolo_incendio",
    label: "Pericolo incendio",
    blurb: "Aree a rischio, fumo sospetto, comportamenti pericolosi",
    icon: Flame,
    accent: "#C15C3D",
  },
  {
    id: "manutenzione_territorio",
    label: "Manutenzione territorio",
    blurb: "Strade, sentieri, fontane, segnaletica da sistemare",
    icon: Wrench,
    accent: "#2E4F3B",
  },
  {
    id: "altro",
    label: "Altro",
    blurb: "Qualcosa che ti preoccupa? Dicci pure.",
    icon: CircleHelp,
    accent: "#5C5C5C",
  },
];

const URGENCIES = [
  { id: "bassa", label: "Bassa", desc: "Può attendere" },
  { id: "media", label: "Media", desc: "Da gestire a breve" },
  { id: "alta", label: "Alta", desc: "Subito, è urgente" },
];

export const Reports = () => {
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    location: "",
    description: "",
    urgency: "media",
    name: "",
    contact: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const reset = () => {
    setSelected(null);
    setForm({
      location: "",
      description: "",
      urgency: "media",
      name: "",
      contact: "",
    });
    setStatus("idle");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) {
      setError("Seleziona prima il tipo di segnalazione.");
      setStatus("error");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/reports`, {
        type: selected,
        location: form.location,
        description: form.description,
        urgency: form.urgency,
        name: form.name || null,
        contact: form.contact || null,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(
        err?.response?.data?.detail ||
          "Qualcosa è andato storto. Riprova o scrivici su WhatsApp."
      );
    }
  };

  const selectedCategory = REPORT_CATEGORIES.find((c) => c.id === selected);

  return (
    <section
      id="segnalazioni"
      data-testid="reports-section"
      className="relative bg-[#1A1A1A] text-white py-24 sm:py-32 overflow-hidden"
    >
      {/* Decorative background */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]">
        <div className="absolute -top-40 -left-40 w-[640px] h-[640px] rounded-full bg-[#2E4F3B] blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[640px] h-[640px] rounded-full bg-[#C15C3D] blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="max-w-3xl mb-14 sm:mb-20">
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#E8C9A0]">
            <span className="inline-block w-10 h-px bg-[#C15C3D] align-middle mr-3" />
            Segnalazioni
          </span>
          <h2
            data-testid="reports-title"
            className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95]"
          >
            Hai un problema?
            <br />
            <span className="italic font-light text-[#E8C9A0]">
              Segnalalo. Lo gestiamo.
            </span>
          </h2>
          <p className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed">
            Una zona da pulire, potature da smaltire, una persona in difficoltà,
            un animale ferito, un pericolo incendio. Ogni segnalazione finisce
            sul tavolo della bacheca e diventa un'azione.
          </p>
        </div>

        {/* Grid layout: categories + form */}
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10">
          {/* Categories */}
          <div className="lg:col-span-7">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#E8C9A0] mb-5">
              {selected ? "1. Tipo selezionato" : "1. Scegli il tipo"}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {REPORT_CATEGORIES.map((c) => {
                const Icon = c.icon;
                const isSelected = selected === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      setSelected(c.id);
                      if (status === "error") setStatus("idle");
                    }}
                    data-testid={`report-category-${c.id}`}
                    className={`group text-left rounded-2xl p-5 border transition-all duration-300 ${
                      isSelected
                        ? "bg-white text-[#1A1A1A] border-white shadow-[0_0_0_4px_rgba(232,201,160,0.18)]"
                        : "bg-white/[0.04] border-white/10 hover:bg-white/[0.08] hover:border-white/25"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <span
                        className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                          isSelected
                            ? "bg-[#1A1A1A] text-white"
                            : "bg-white/5 text-[#E8C9A0] group-hover:bg-white/10"
                        }`}
                      >
                        <Icon className="w-5 h-5" strokeWidth={2} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`font-display font-bold text-base sm:text-lg ${
                            isSelected ? "text-[#1A1A1A]" : "text-white"
                          }`}
                        >
                          {c.label}
                        </div>
                        <div
                          className={`text-xs mt-1 leading-relaxed ${
                            isSelected ? "text-[#5C5C5C]" : "text-white/55"
                          }`}
                        >
                          {c.blurb}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Soft note */}
            <div className="mt-6 flex items-start gap-3 text-xs text-white/50 leading-relaxed">
              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#E8C9A0]" />
              <p>
                Ogni segnalazione viene letta. Le più urgenti vengono gestite
                subito sulla bacheca, le altre nei tempi tecnici. Se serve
                rispondiamo al contatto che lasci.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#E8C9A0] mb-5">
              2. Compila la segnalazione
            </div>

            {status === "success" ? (
              <div
                data-testid="reports-success"
                className="rounded-3xl bg-[#2E4F3B] text-white p-8 sm:p-10"
              >
                <div className="w-14 h-14 rounded-full bg-white/15 flex items-center justify-center mb-5">
                  <Check className="w-7 h-7 text-[#E8C9A0]" />
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-black leading-tight">
                  Segnalazione ricevuta.
                </h3>
                <p className="mt-3 text-white/80 leading-relaxed">
                  Grazie. La passiamo alla bacheca: chi può intervenire si fa
                  avanti. Se vuoi metterti in gioco, candidati come volontario.
                </p>
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <a
                    href="/partecipa"
                    data-testid="reports-success-volunteer-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C15C3D] text-white font-bold px-6 py-3 hover:bg-[#a64830] transition-all shadow-[0_8px_24px_rgba(193,92,61,0.4)]"
                  >
                    Diventa volontario
                    <ArrowRight className="w-4 h-4" />
                  </a>
                  <button
                    type="button"
                    onClick={reset}
                    data-testid="reports-new-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 text-white font-semibold px-6 py-3 hover:bg-white/20 border border-white/20 transition-all"
                  >
                    Nuova segnalazione
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="reports-form"
                className="rounded-3xl bg-white/[0.04] border border-white/10 backdrop-blur p-6 sm:p-8 space-y-5"
              >
                {/* Selected pill */}
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-white/50">Tipo:</span>
                  <span
                    data-testid="reports-selected-type"
                    className={`font-semibold ${
                      selected ? "text-[#E8C9A0]" : "text-white/40"
                    }`}
                  >
                    {selectedCategory ? selectedCategory.label : "non selezionato"}
                  </span>
                </div>

                {/* Location */}
                <div>
                  <label
                    htmlFor="location"
                    className="block text-[10px] font-bold uppercase tracking-[0.18em] text-white/60 mb-2"
                  >
                    Località / Indirizzo
                  </label>
                  <input
                    id="location"
                    name="location"
                    type="text"
                    required
                    minLength={2}
                    maxLength={240}
                    value={form.location}
                    onChange={handleChange}
                    data-testid="reports-input-location"
                    placeholder="Es. Via dei Cipressi, Asciano · Loc. Mucigliani"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#E8C9A0]/60 focus:ring-2 focus:ring-[#E8C9A0]/15 transition-all"
                  />
                </div>

                {/* Description */}
                <div>
                  <label
                    htmlFor="description"
                    className="block text-[10px] font-bold uppercase tracking-[0.18em] text-white/60 mb-2"
                  >
                    Descrizione
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    required
                    minLength={5}
                    maxLength={5000}
                    rows={4}
                    value={form.description}
                    onChange={handleChange}
                    data-testid="reports-input-description"
                    placeholder="Cosa hai visto? Da quando? Foto se ne hai…"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#E8C9A0]/60 focus:ring-2 focus:ring-[#E8C9A0]/15 transition-all resize-none"
                  />
                </div>

                {/* Urgency */}
                <div>
                  <div className="block text-[10px] font-bold uppercase tracking-[0.18em] text-white/60 mb-2">
                    Urgenza
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {URGENCIES.map((u) => {
                      const active = form.urgency === u.id;
                      return (
                        <button
                          type="button"
                          key={u.id}
                          onClick={() => setForm({ ...form, urgency: u.id })}
                          data-testid={`reports-urgency-${u.id}`}
                          className={`rounded-xl border px-3 py-3 text-left transition-all ${
                            active
                              ? u.id === "alta"
                                ? "border-[#C15C3D] bg-[#C15C3D]/15"
                                : "border-[#E8C9A0]/60 bg-[#E8C9A0]/10"
                              : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
                          }`}
                        >
                          <div
                            className={`text-xs font-bold uppercase tracking-wider ${
                              active
                                ? u.id === "alta"
                                  ? "text-[#E8C9A0]"
                                  : "text-[#E8C9A0]"
                                : "text-white"
                            }`}
                          >
                            {u.label}
                          </div>
                          <div className="text-[10px] text-white/50 mt-0.5 leading-tight">
                            {u.desc}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Optional fields */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-[10px] font-bold uppercase tracking-[0.18em] text-white/60 mb-2"
                    >
                      Il tuo nome (facolt.)
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      maxLength={120}
                      value={form.name}
                      onChange={handleChange}
                      data-testid="reports-input-name"
                      placeholder="Anonimo va bene"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#E8C9A0]/60 focus:ring-2 focus:ring-[#E8C9A0]/15 transition-all"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact"
                      className="block text-[10px] font-bold uppercase tracking-[0.18em] text-white/60 mb-2"
                    >
                      Contatto (facolt.)
                    </label>
                    <input
                      id="contact"
                      name="contact"
                      type="text"
                      maxLength={200}
                      value={form.contact}
                      onChange={handleChange}
                      data-testid="reports-input-contact"
                      placeholder="Tel o email per ricontattarti"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#E8C9A0]/60 focus:ring-2 focus:ring-[#E8C9A0]/15 transition-all"
                    />
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === "loading" || !selected}
                  data-testid="reports-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#E8C9A0] text-[#1A1A1A] font-bold px-8 py-4 hover:bg-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Invio in corso…
                    </>
                  ) : (
                    <>
                      Invia segnalazione
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>

                {status === "error" && (
                  <div
                    data-testid="reports-error"
                    className="flex items-start gap-3 bg-[#C15C3D]/15 border border-[#C15C3D]/40 rounded-xl p-4"
                  >
                    <AlertCircle className="w-5 h-5 text-[#E8C9A0] shrink-0 mt-0.5" />
                    <div className="text-sm text-white">{error}</div>
                  </div>
                )}

                <p className="text-[11px] text-white/40 leading-relaxed">
                  Inviando accetti che le info utili siano condivise nel gruppo
                  operativo. Niente spam, niente diffusione esterna.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
